import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { voucherAPI, categoryAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { FilePlus, Trash2, Plus } from 'lucide-react';
import BillUpload from '../components/BillUpload';
import SignaturePad from '../components/SignaturePad';

const VOUCHER_TYPES = [
  'Payment Voucher', 'Receipt Voucher', 'Contra Voucher',
  'Journal Voucher', 'Advance Voucher', 'Advance Settlement Voucher'
];
const PAYMENT_MODES = ['Cash', 'UPI', 'Bank Transfer', 'Cheque', 'Card'];

export default function AddVoucherPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    voucherType: 'Payment Voucher',
    date: new Date().toISOString().split('T')[0],
    amount: '',
    paymentMode: 'Cash',
    transactionRef: '',
    category: '',
    subCategory: '',
    description: '',
    notes: '',
    items: [],
    payments: [],
  });
  const [splitMode, setSplitMode] = useState(false);
  const [billFiles, setBillFiles] = useState([]);
  const [preparedSignature, setPreparedSignature] = useState(null);
  const [budgetMap, setBudgetMap] = useState({});

  useEffect(() => {
    Promise.all([
      categoryAPI.list(),
      categoryAPI.budgetStatus()
    ]).then(([catRes, budgetRes]) => {
      setCategories(catRes.data.categories);
      const map = {};
      budgetRes.data.statuses.forEach(s => { map[s.categoryId] = s; });
      setBudgetMap(map);
    }).catch(() => toast.error('Failed to load categories'));
  }, []);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const addItem = () => {
    setForm(prev => ({
      ...prev,
      items: [...prev.items, { _cid: Date.now() + '-' + Math.random(), category: '', subCategory: '', description: '', amount: '' }]
    }));
  };

  const updateItem = (index, field, value) => {
    setForm(prev => {
      const items = [...prev.items];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, items };
    });
  };

  const removeItem = (index) => {
    setForm(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.description.trim()) { toast.error('Description is required'); return; }
    if (!form.amount && form.items.length === 0) { toast.error('Amount is required'); return; }
    if (!form.category && form.items.length === 0) { toast.error('Category is required'); return; }

    setLoading(true);
    try {
      const payload = {
        ...form,
        amount: splitMode
          ? form.items.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0)
          : parseFloat(form.amount),
        items: splitMode ? form.items.map(i => ({ ...i, amount: parseFloat(i.amount) || 0 })) : [],
        bills: billFiles.map(f => ({
          filename: f.filename,
          originalName: f.originalName,
          mimetype: f.mimetype,
          size: f.size,
          data: f.data
        })),
      };

      const res = await voucherAPI.create(payload);
      const voucherId = res.data.voucher._id;

      // Save signature if captured
      if (preparedSignature) {
        await voucherAPI.saveSignature(voucherId, 'preparedBy', preparedSignature).catch(() => toast.error('Failed to save signature'));
      }

      toast.success(`Voucher ${res.data.voucher.voucherId} created`);
      navigate('/vouchers');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create voucher');
    } finally {
      setLoading(false);
    }
  };

  const totalSplitAmount = form.items.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);

  return (
    <div className="max-w-2xl mx-auto" data-testid="add-voucher-page">
      <div className="flex items-center gap-2 mb-4">
        <FilePlus className="w-5 h-5 text-slate-700" />
        <h1 className="text-xl font-bold tracking-tight text-slate-900">Add Voucher</h1>
      </div>

      <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-lg p-4 md:p-6 space-y-4">
        {/* Row 1: Type + Date */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Voucher Type</Label>
            <Select value={form.voucherType} onValueChange={(v) => handleChange('voucherType', v)}>
              <SelectTrigger className="mt-1" data-testid="voucher-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {VOUCHER_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Date</Label>
            <Input
              type="date"
              value={form.date}
              onChange={(e) => handleChange('date', e.target.value)}
              className="mt-1"
              data-testid="voucher-date-input"
            />
          </div>
        </div>

        {/* Row 2: Amount + Payment */}
        {!splitMode && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Amount (₹)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={form.amount}
                onChange={(e) => handleChange('amount', e.target.value)}
                placeholder="0.00"
                className="mt-1"
                data-testid="voucher-amount-input"
              />
            </div>
            <div>
              <Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Payment Mode</Label>
              <Select value={form.paymentMode} onValueChange={(v) => handleChange('paymentMode', v)}>
                <SelectTrigger className="mt-1" data-testid="payment-mode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_MODES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Transaction Ref */}
        {form.paymentMode !== 'Cash' && !splitMode && (
          <div>
            <Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Transaction Reference</Label>
            <Input
              value={form.transactionRef}
              onChange={(e) => handleChange('transactionRef', e.target.value)}
              placeholder="UPI ID / Cheque No / NEFT Ref"
              className="mt-1"
              data-testid="transaction-ref-input"
            />
          </div>
        )}

        {/* Category */}
        {!splitMode && (
          <div>
            <Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Category</Label>
            <Select value={form.category} onValueChange={(v) => handleChange('category', v)}>
              <SelectTrigger className="mt-1" data-testid="category-select">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map(c => <SelectItem key={c._id} value={c._id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {/* Budget warning for selected category */}
            {form.category && budgetMap[form.category] && budgetMap[form.category].budget > 0 && (
              <BudgetIndicator b={budgetMap[form.category]} />
            )}
          </div>
        )}

        {/* Description */}
        <div>
          <Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Description *</Label>
          <textarea
            value={form.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Enter expense description (mandatory)"
            rows={2}
            className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900"
            data-testid="description-input"
          />
        </div>

        {/* Split Expense Toggle */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              setSplitMode(!splitMode);
              if (!splitMode && form.items.length === 0) addItem();
            }}
            className="text-xs text-slate-600 hover:text-slate-900 underline"
            data-testid="toggle-split-btn"
          >
            {splitMode ? 'Switch to single amount' : 'Split into multiple categories'}
          </button>
        </div>

        {/* Split Items */}
        {splitMode && (
          <div className="space-y-2 border border-slate-200 rounded-md p-3 bg-slate-50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Expense Items</span>
              <span className="text-xs font-medium text-slate-900">Total: ₹{totalSplitAmount.toLocaleString('en-IN')}</span>
            </div>
            {form.items.map((item, idx) => (
              <div key={item._cid} className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-4">
                  <Select value={item.category} onValueChange={(v) => updateItem(idx, 'category', v)}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(c => <SelectItem key={c._id} value={c._id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-4">
                  <Input
                    value={item.description}
                    onChange={(e) => updateItem(idx, 'description', e.target.value)}
                    placeholder="Description"
                    className="h-8 text-xs"
                  />
                </div>
                <div className="col-span-3">
                  <Input
                    type="number"
                    value={item.amount}
                    onChange={(e) => updateItem(idx, 'amount', e.target.value)}
                    placeholder="₹ 0"
                    className="h-8 text-xs"
                  />
                </div>
                <div className="col-span-1">
                  <button type="button" onClick={() => removeItem(idx)} className="p-1 hover:bg-red-50 rounded">
                    <Trash2 className="w-3.5 h-3.5 text-red-500" />
                  </button>
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={addItem}
              className="flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900 mt-1"
              data-testid="add-item-btn"
            >
              <Plus className="w-3 h-3" /> Add Item
            </button>
          </div>
        )}

        {/* Notes */}
        <div>
          <Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Notes (optional)</Label>
          <Input
            value={form.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
            placeholder="Additional notes"
            className="mt-1"
            data-testid="notes-input"
          />
        </div>

        {/* Bill Upload */}
        <BillUpload files={billFiles} onChange={setBillFiles} />

        {/* Signature - Prepared By */}
        <SignaturePad
          label="Prepared By (Signature)"
          onSave={(data) => { setPreparedSignature(data); toast.success('Signature captured'); }}
        />

        {/* Amount indicator */}
        {(form.amount || totalSplitAmount > 0) && (
          <ApprovalRequirement amount={splitMode ? totalSplitAmount : parseFloat(form.amount) || 0} />
        )}

        {/* Submit */}
        <div className="flex gap-2 pt-2">
          <Button
            type="submit"
            disabled={loading}
            className="bg-slate-900 hover:bg-slate-800 text-white flex-1"
            data-testid="submit-voucher-btn"
          >
            {loading ? 'Creating...' : 'Create Voucher'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/vouchers')}
            data-testid="cancel-voucher-btn"
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}


function BudgetIndicator({ b }) {
  const isExceeded = b.alertLevel === 'exceeded';
  const isWarning = b.alertLevel === 'warning';
  const barColor = isExceeded ? 'bg-red-500' : isWarning ? 'bg-amber-500' : 'bg-slate-400';

  let bgColor = 'bg-slate-50 border-slate-200';
  let pctColor = 'text-slate-600';
  if (isExceeded) { bgColor = 'bg-red-50 border-red-200'; pctColor = 'text-red-600'; }
  else if (isWarning) { bgColor = 'bg-amber-50 border-amber-200'; pctColor = 'text-amber-600'; }

  return (
    <div className={`mt-1.5 rounded-md border p-2 ${bgColor}`} data-testid="budget-indicator">
      <div className="flex items-center justify-between text-[10px] mb-1">
        <span className="text-slate-600">Budget: ₹{b.spent.toLocaleString('en-IN')} / ₹{b.budget.toLocaleString('en-IN')}</span>
        <span className={`font-bold ${pctColor}`}>
          {b.pct}% used
        </span>
      </div>
      <div className="w-full h-1 bg-white/80 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${barColor}`} style={{ width: `${Math.min(b.pct, 100)}%` }} />
      </div>
      {isExceeded && <p className="text-[10px] text-red-600 font-medium mt-1">Budget exceeded — voucher will still be created but flagged.</p>}
      {isWarning && <p className="text-[10px] text-amber-600 font-medium mt-1">Approaching budget limit.</p>}
    </div>
  );
}


function getApprovalLabel(amount) {
  if (amount < 500) return 'Auto-approved (under ₹500)';
  if (amount < 5000) return 'Manager approval required';
  if (amount < 10000) return 'Admin approval required';
  return 'Admin approval required + Verification flag';
}

function ApprovalRequirement({ amount }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-md p-3" data-testid="approval-requirement">
      <p className="text-xs text-slate-500">Approval Requirement:</p>
      <p className="text-sm font-medium text-slate-900">{getApprovalLabel(amount)}</p>
    </div>
  );
}
